// Servicio de tracking de actividades para usuarios Demo
// Implementa batching, throttling y envío asíncrono para no degradar performance

import { getUser, isDemo } from './auth';
import { getApiBaseUrl } from '../config';

interface ActivityEvent {
  action: string;
  element?: string;
  element_id?: number | null;
  element_name?: string;
  screen?: string;
  room_id?: number | null;
  tab?: string;
  device_id?: number | null;
  filter?: string;
  value?: any;
  attempted_action?: string;
  blocked?: boolean;
  block_reason?: string;
  success?: boolean;
  error?: string;
  timestamp?: string;
}

class DemoActivityTracker {
  private buffer: ActivityEvent[] = [];
  private sendInterval: number = 5000; // 5 segundos
  private maxBufferSize: number = 20; // Máximo de eventos antes de enviar inmediatamente
  private intervalId: NodeJS.Timeout | null = null;
  private isSending: boolean = false;
  private lastScrollTime: number = 0;
  private scrollThrottle: number = 1000; // 1 segundo entre eventos de scroll
  private lastClickTime: Map<string, number> = new Map();
  private clickThrottle: number = 200; // 200ms entre clicks del mismo elemento

  constructor() {
    // Iniciar intervalo de envío automático
    this.startAutoSend();
    
    // Guardar eventos pendientes antes de cerrar la página
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.sendBatchSync();
      });
      
      // También enviar cuando la página se oculta
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.sendBatch();
        }
      });
    }
  }

  private startAutoSend() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    
    this.intervalId = setInterval(() => {
      if (this.buffer.length > 0) {
        this.sendBatch();
      }
    }, this.sendInterval);
  }

  /**
   * Registra una actividad del usuario Demo
   */
  track(event: ActivityEvent): void {
    // Verificar que el usuario es Demo
    const user = getUser();
    if (!user || !isDemo(user)) {
      return; // No hacer nada si no es Demo
    }

    // Throttling para scroll (máximo 1 vez por segundo)
    if (event.action === 'scroll') {
      const now = Date.now();
      if (now - this.lastScrollTime < this.scrollThrottle) {
        return; // Ignorar si es muy frecuente
      }
      this.lastScrollTime = now;
    }

    // Throttling para clicks del mismo elemento (máximo 1 vez cada 200ms)
    if (event.action === 'click' && event.element_id) {
      const key = `${event.element}_${event.element_id}`;
      const now = Date.now();
      const lastClick = this.lastClickTime.get(key) || 0;
      if (now - lastClick < this.clickThrottle) {
        return; // Ignorar si es muy frecuente
      }
      this.lastClickTime.set(key, now);
    }

    // Agregar timestamp
    event.timestamp = new Date().toISOString();

    // Agregar al buffer
    this.buffer.push(event);

    // Si el buffer alcanza el tamaño máximo, enviar inmediatamente
    if (this.buffer.length >= this.maxBufferSize) {
      this.sendBatch();
    }
  }

  /**
   * Envía el batch de eventos al backend de forma asíncrona
   */
  private async sendBatch(): Promise<void> {
    if (this.isSending || this.buffer.length === 0) {
      return;
    }

    this.isSending = true;
    const batch = this.buffer.splice(0); // Extraer todos los eventos del buffer

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Si no hay token, guardar en localStorage para enviar después
        this.saveToLocalStorage(batch);
        this.isSending = false;
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/demo/activity-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Si había eventos guardados en localStorage, intentar enviarlos también
      this.sendPendingFromLocalStorage();
      
    } catch (error) {
      // Si falla, guardar en localStorage para reintentar después
      console.warn('[DemoActivityTracker] Error enviando batch, guardando en localStorage:', error);
      this.saveToLocalStorage(batch);
    } finally {
      this.isSending = false;
    }
  }

  /**
   * Envía el batch de forma síncrona (para beforeunload)
   * Usa fetch con keepalive para permitir headers de autorización
   */
  private sendBatchSync(): void {
    if (this.buffer.length === 0) {
      return;
    }

    const batch = this.buffer.splice(0);
    const token = localStorage.getItem('token');
    
    if (!token) {
      this.saveToLocalStorage(batch);
      return;
    }

    // Usar fetch con keepalive para envío síncrono antes de cerrar la página
    // keepalive permite que la petición continúe incluso después de que la página se cierre
    try {
      fetch(`${getApiBaseUrl()}/demo/activity-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(batch),
        keepalive: true // Permite que la petición continúe después de cerrar la página
      }).catch(() => {
        // Si falla, guardar en localStorage (silenciosamente, no queremos errores en consola)
        this.saveToLocalStorage(batch);
      });
    } catch (error) {
      // Fallback: guardar en localStorage
      this.saveToLocalStorage(batch);
    }
  }

  /**
   * Guarda eventos en localStorage para enviar después
   */
  private saveToLocalStorage(batch: ActivityEvent[]): void {
    try {
      const existing = localStorage.getItem('demo_activities_pending');
      const pending = existing ? JSON.parse(existing) : [];
      pending.push(...batch);
      
      // Limitar a 100 eventos pendientes para no llenar localStorage
      if (pending.length > 100) {
        pending.splice(0, pending.length - 100);
      }
      
      localStorage.setItem('demo_activities_pending', JSON.stringify(pending));
    } catch (error) {
      console.warn('[DemoActivityTracker] Error guardando en localStorage:', error);
    }
  }

  /**
   * Intenta enviar eventos pendientes de localStorage
   */
  private async sendPendingFromLocalStorage(): Promise<void> {
    try {
      const pendingStr = localStorage.getItem('demo_activities_pending');
      if (!pendingStr) {
        return;
      }

      const pending = JSON.parse(pendingStr);
      if (pending.length === 0) {
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/demo/activity-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(pending)
      });

      if (response.ok) {
        // Si se envió correctamente, limpiar localStorage
        localStorage.removeItem('demo_activities_pending');
      }
    } catch (error) {
      // Si falla, dejar en localStorage para intentar después
      console.warn('[DemoActivityTracker] Error enviando pendientes:', error);
    }
  }

  /**
   * Métodos de conveniencia para tipos de eventos comunes
   */
  trackClick(element: string, elementId: number | null, elementName: string, context: {
    screen?: string;
    roomId?: number | null;
    tab?: string;
    deviceId?: number | null;
    attemptedAction?: string;
    blocked?: boolean;
    blockReason?: string;
    success?: boolean;
    error?: string;
    value?: any;
  }): void {
    this.track({
      action: 'click',
      element,
      element_id: elementId,
      element_name: elementName,
      screen: context.screen,
      room_id: context.roomId,
      tab: context.tab,
      device_id: context.deviceId,
      attempted_action: context.attemptedAction,
      blocked: context.blocked,
      block_reason: context.blockReason,
      success: context.success,
      error: context.error,
      value: context.value
    });
  }

  trackNavigation(screen: string, params?: {
    roomId?: number | null;
    deviceId?: number | null;
    tab?: string;
  }): void {
    this.track({
      action: 'navigation',
      screen,
      room_id: params?.roomId,
      device_id: params?.deviceId,
      tab: params?.tab
    });
  }

  trackViewChange(view: string, context?: {
    screen?: string;
    roomId?: number | null;
  }): void {
    this.track({
      action: 'view_change',
      element: 'view_selector',
      element_name: view,
      screen: context?.screen,
      room_id: context?.roomId
    });
  }

  trackFilterChange(filter: string, value: any, context?: {
    screen?: string;
    tab?: string;
    roomId?: number | null;
    deviceId?: number | null;
  }): void {
    this.track({
      action: 'filter_change',
      filter,
      value: typeof value === 'object' ? JSON.stringify(value) : value,
      screen: context?.screen,
      tab: context?.tab,
      device_id: context?.deviceId,
      room_id: context?.roomId
    });
  }

  trackSearch(query: string, context?: {
    screen?: string;
    resultsCount?: number;
    tab?: string;
  }): void {
    this.track({
      action: 'search',
      element: 'search_input',
      element_name: query.substring(0, 50), // Limitar longitud
      screen: context?.screen,
      value: context?.resultsCount
    });
  }

  trackModal(action: 'open' | 'close', modalName: string, context?: {
    screen?: string;
    deviceId?: number | null;
    roomId?: number | null;
  }): void {
    this.track({
      action: `modal_${action}`,
      element: 'modal',
      element_name: modalName,
      screen: context?.screen,
      device_id: context?.deviceId,
      room_id: context?.roomId
    });
  }

  trackTabChange(tab: string, screen: string): void {
    this.track({
      action: 'tab_change',
      element: 'tab_button',
      element_name: tab,
      screen,
      tab
    });
  }

  /**
   * Limpia el buffer (útil para testing o reset)
   */
  clear(): void {
    this.buffer = [];
  }

  /**
   * Destruye el tracker (limpia intervalos)
   */
  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    // Enviar cualquier evento pendiente antes de destruir
    this.sendBatchSync();
  }
}

// Instancia singleton
let trackerInstance: DemoActivityTracker | null = null;

export const getDemoActivityTracker = (): DemoActivityTracker => {
  if (!trackerInstance) {
    trackerInstance = new DemoActivityTracker();
  }
  return trackerInstance;
};

export default getDemoActivityTracker;

